/**
 * @author Lorenzo Muscariello <lmuscariello@ciaopeople.com>
 */
import { inEnum, isNumber } from "@ciaopeople/cp-shatsu/TypeChecking";
import { PromiseOnSteroids } from "../../../../lib/utils/PromiseOnSteroids.js";
import { VideoRef } from "../../../player/MediaResource.js";
import { AdBreakType, AdUnitType, VASTTagAdUnit } from "../../AdUnit.js";
import { AbstractImaController, WrappedImaAdRequest, WrappedImaAdsManager } from "../AbstractImaController.js";
import { AdItemControls, AdItemStatus, ImaAdItem, ProgressData, ProgressDataListener } from "../ImaAdItem.js";

/**
 * ImaAdItem associated with a VASTTagAdUnit
 */
export interface VASTImaAdItem extends ImaAdItem<VASTTagAdUnit> {
    isGroup: boolean
    isEmpty: boolean;
}

/**
 * Implementation of VASTImaAdItem based on a single `VASTTagAdUnit`
 */
export class SingleVASTImaAdItem implements VASTImaAdItem {



    private currentStatus: AdItemStatus;
    private loadPromise: Promise<WrappedImaAdsManager>;
    private evtsOfInterest: google.ima.AdEvent.Type[];
    private adItemControlsPromise: Promise<AdItemControls>;
    private wrappedRequest: WrappedImaAdRequest;    



    public readonly isGroup: boolean = false;
    public readonly isEmpty: boolean = false;
    /**
     * Builds a new SingleVASTImaAdItem
     * @param unit VAST Tag Ad unit associated to this item
     * @param imaAdController parent ima controller
     * @param initOnLoad if true `WrappedImaAdsManager` init si called on load method
     */
    constructor(protected unit: VASTTagAdUnit,
        private videoInfo: VideoRef,
        private imaAdController: AbstractImaController,
        private initOnLoad: boolean) {
        this.currentStatus = AdItemStatus.INITIALIZED;
        this.evtsOfInterest = [
            google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
            google.ima.AdEvent.Type.USER_CLOSE,
            google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
            google.ima.AdEvent.Type.SKIPPED,
            google.ima.AdEvent.Type.CLICK,
            google.ima.AdEvent.Type.RESUMED,
            google.ima.AdEvent.Type.PAUSED,
            google.ima.AdEvent.Type.LOADED,
            google.ima.AdEvent.Type.STARTED,
            google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
            google.ima.AdEvent.Type.COMPLETE,
            google.ima.AdEvent.Type.VOLUME_CHANGED,
            google.ima.AdEvent.Type.AD_PROGRESS
        ];
        this.loadPromise = null;
        this.adItemControlsPromise = null;
        this.wrappedRequest = imaAdController.newWrappedAdRequest(unit,videoInfo);
    }


    public get status(): AdItemStatus {
        return this.currentStatus;
    }


    public get units(): VASTTagAdUnit[] {
        return [this.unit];
    }


    public prepare(): void {
        this.wrappedRequest.prepareRequest();
    }

    /**
     * Executes the ad request and gives back a `WrappedImaAdsManager`
     * If `initOnLoad` is true also initializes the manager
     * this method could be also invoked in a fire and forget fashion to achieve 
     * prelaoding
     * @returns a promise containing a wrapped `google.ima.AdManager` or a rejected promise in case of ima LoadError
     */
    public load(): Promise<WrappedImaAdsManager> {
        if (!this.loadPromise) {
            this.currentStatus = AdItemStatus.LOADING;
            this.loadPromise = this.wrappedRequest.doRequest().then(w => {
                if(this.initOnLoad) w.init();
                this.currentStatus = AdItemStatus.LOADED;
                return w;
            }).catch(e => {
                console.error("Error loading adv", e)
                return Promise.reject(AdItemStatus.LOAD_ERROR);
            });
        }
        return this.loadPromise;
    }



    /**
     * Starts the playback of the ad creative.
     * Awaits for unit to be loaded and then starts the `WrappedImaAdManager`
     * @returns AdItem playback controls
     */
    public start(): Promise<AdItemControls> {
        if (!this.adItemControlsPromise) {

            this.adItemControlsPromise = this.load().then(wrap => {
                let itemPlayStatus: boolean = false;
                let itemStarted: boolean = false;
                let alreadyCompleted: boolean = false;
                let listeners: ProgressDataListener[] = [];
                const adInfoResolvable = PromiseOnSteroids.getResolvable<google.ima.Ad>();
                const completionResolvable = PromiseOnSteroids.getResolvable<AdItemStatus>();
                const triggerPoints = [20, 40, 60, 80];
                const off = () => {

                    if (!alreadyCompleted) {

                        alreadyCompleted = true;
                        adInfoResolvable.resolve(null);
                        this.evtsOfInterest.forEach(evt => wrap.adsManager.removeEventListener(evt, evtLst));
                        wrap.adsManager.removeEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, errLst);
                        this.imaAdController.destroyAdManager(wrap);
                    }
                }
                const evtLst = (evt: google.ima.AdEvent) => {
                    const info = evt.getAd();
                    if (info) adInfoResolvable.resolve(info);
                    switch (evt.type) {
                        case google.ima.AdEvent.Type.ALL_ADS_COMPLETED:
                        case google.ima.AdEvent.Type.USER_CLOSE:
                        case google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED:
                        // case google.ima.AdEvent.Type.COMPLETE:
                        case google.ima.AdEvent.Type.SKIPPED:
                            console.log("[AD-EVT]", evt);
                            off();
                            completionResolvable.resolve(AdItemStatus.ENDED);
                            break;
                        case google.ima.AdEvent.Type.CLICK:
                            console.log("[AD-EVT]", evt);
                            break;
                        case google.ima.AdEvent.Type.VOLUME_CHANGED:
                            console.log("[AD-EVT] VOLUME CHANGED", evt);
                            this.imaAdController.getAdContainerControls().signalAudioStatus(wrap.adsManager.getVolume() == 0)
                            break;
                        case google.ima.AdEvent.Type.RESUMED:
                            console.log("[AD-EVT]", evt);
                            itemPlayStatus = true;
                            this.imaAdController.getAdContainerControls().signalPlayStatus(itemPlayStatus);
                            break;
                        case google.ima.AdEvent.Type.PAUSED:
                            console.log("[AD-EVT]", evt);
                            itemPlayStatus = false;
                            this.imaAdController.getAdContainerControls().signalPlayStatus(itemPlayStatus);
                            break;
                        case google.ima.AdEvent.Type.LOADED:
                            console.log("[AD-EVT]", evt);
                            break;
                        case google.ima.AdEvent.Type.STARTED:
                            console.log("[AD-EVT]", evt);
                            this.imaAdController.getAdContainerControls().refresh(wrap.adsManager, evt.getAd());
                            itemStarted = true;
                            itemPlayStatus = true;
                            this.imaAdController.getAdContainerControls().signalPlayStatus(itemPlayStatus);
                            this.currentStatus = AdItemStatus.PLAYING;
                            break;
                        case google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED:
                            console.log("[AD-EVT]", evt);
                            break;
                        case google.ima.AdEvent.Type.AD_PROGRESS:
                            const currentTime: number = evt.getAdData()?.currentTime;
                            const duration: number = evt.getAdData()?.duration;
                            if (itemStarted && !(typeof currentTime == "undefined") && !(typeof duration == "undefined")) {
                                const percentage = Math.floor((currentTime * 100) / duration);
                                if (triggerPoints.length > 0 && percentage >= triggerPoints[0]) {
                                    listeners.forEach((lst) => {
                                        lst({
                                            currentTime: currentTime,
                                            progress: percentage,
                                            duration: duration
                                        })
                                    })
                                    triggerPoints.shift();
                                }
                            }
                            break;
                    }
                };
                const errLst = (evt: google.ima.AdErrorEvent) => {
                    console.error("[AD-EVT] ERROR: ", evt.getError()?.getMessage(), evt.getError()?.getInnerError());
                    off();
                    completionResolvable.resolve(AdItemStatus.PLAY_ERROR);
                }
                this.evtsOfInterest.forEach(evt => wrap.adsManager.addEventListener(evt, evtLst));
                wrap.adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, errLst);

                console.log("[AD-EVT] AD-UNIT STARTED", this.unit);
                wrap.init();
                return wrap.start().then(_ => {
                    return {
                        completionPromise: completionResolvable.promise,
                        adInfo: () => adInfoResolvable.promise,
                        close: () => {

                            if (itemStarted && itemPlayStatus) {
                                wrap.close();
                                return Promise.resolve(true);
                            }
                            else return Promise.resolve(false);
                        },
                        pause: () => {
                            if (itemStarted && itemPlayStatus) {
                                wrap.adsManager.pause();
                            }
                            return Promise.resolve();
                        },
                        destroy: () => {
                            //auto-destroys on end
                            off();
                            return Promise.resolve();
                        },
                        resume: () => {
                            if (itemStarted && !itemPlayStatus) {
                                wrap.adsManager.resume();
                            }
                            return Promise.resolve();
                        },
                        togglePlay: () => {
                            let returnStatus = true;
                            if (itemStarted) {
                                returnStatus = !itemPlayStatus
                                itemPlayStatus ? wrap.adsManager.pause() : wrap.adsManager.resume();
                            }
                            return Promise.resolve(returnStatus);
                        },
                        toggleAudio: () => {
                            const audioOn = wrap.adsManager.getVolume() > 0;
                            if (audioOn) {
                                wrap.adsManager.setVolume(0);
                            }
                            else wrap.adsManager.setVolume(1);
                            return Promise.resolve(!audioOn);
                        },
                        alreadyCompleted: () => {
                            return alreadyCompleted;
                        },
                        volume: (volume?: number) => {
                            if (!(typeof volume == "undefined"))
                                wrap.adsManager.setVolume(volume);
                            return Promise.resolve(wrap.adsManager.getVolume());
                        },
                        unmute: () => {
                            //?WHY: 0.25
                            wrap.adsManager.setVolume(0.25);
                            return Promise.resolve(true);
                        },
                        muted: (enable?: boolean) => {
                            if(typeof enable !== "undefined") {
                                wrap.adsManager.setVolume(enable?0:1);
                            }
                            return Promise.resolve(wrap.adsManager.getVolume() > 0);
                        },
                        onProgress: (lst: ProgressDataListener) => {
                            listeners.push(lst);
                        },
                        offProgress: (lst: ProgressDataListener) => {
                            listeners = listeners.filter((e) => e != lst);
                        }
                    }
                })


            }).catch(err => {
                console.log("[AD-EV] AD_PLAY_ERROR: ", this.unit);
                const useStatus: AdItemStatus = err && isNumber(err) && inEnum(err, AdItemStatus) ? err : AdItemStatus.LOAD_ERROR;
                return Promise.resolve(buildFixedStatusNoOpControls(useStatus));
            });
        }
        return this.adItemControlsPromise;
    }

}


function buildFixedStatusNoOpControls(fixedStatus: AdItemStatus): AdItemControls {
    return {
        completionPromise: Promise.resolve(fixedStatus),
        adInfo: () => Promise.resolve(null),
        close: () => Promise.resolve(true),
        pause: () => {
            return Promise.resolve();
        },
        destroy: () => {
            return Promise.resolve();
        },
        resume: () => {
            return Promise.resolve();
        },
        togglePlay: () => {
            return Promise.resolve(true);
        },
        toggleAudio: () => {
            return Promise.resolve(false);
        },
        alreadyCompleted: () => {
            return true;
        },
        volume: (volume?: number) => {
            return Promise.resolve(volume)
        },
        unmute: () => {
            return Promise.resolve(false);
        },
        muted: (enable?: boolean) =>  {
            return Promise.resolve(!!enable)
        },
        onProgress: (lst: ProgressDataListener) => {

        },
        offProgress: (lst: ProgressDataListener) => {

        }
    }
}


/**
 * Null Vast Ima Ad Item implementation
 * could be used as placeholder or replacement implementation
 */
export class NullVASTImaAdItem implements VASTImaAdItem {

    private unit: VASTTagAdUnit;

    public readonly isGroup: boolean = false;
    public readonly isEmpty: boolean = true;

    constructor(protected breakType: AdBreakType, protected unitLabel: string) {
        this.unit = {
            breakType: breakType,
            unitType: AdUnitType.VAST_TAG,
            label: unitLabel,
            baseUrl: "/#adUnit",
            params: {
                sz: "640x480",
                iu: "adunit"
            }
        };
    }

    public get units(): VASTTagAdUnit[] {
        return [this.unit];
    }

    public get status(): AdItemStatus {
        return AdItemStatus.ENDED;
    }


    load(): Promise<WrappedImaAdsManager> {
        return Promise.resolve(null);
    }
    start(): Promise<AdItemControls> {
        return Promise.resolve(buildFixedStatusNoOpControls(AdItemStatus.ENDED));
    }

    prepare(): void {

    }

}



export enum GroupSuccessCriteria {

    AT_LEAST_ONE = "atleast_one",
    ALL = "all"
}


export enum ItemPrepareStrategy {

    ALAP = "alap", 
    BEFORE_PREV_ITEM_END = "before_item_end"

}

/**
 * This implementation could be used to chain multiple 
 * `VASTImaAdItem` and treat them as a single ad item.
 */
export class GroupedVASTImaAdItems implements VASTImaAdItem {



    private wraps: (() => Promise<WrappedImaAdsManager>)[];
    private currentIndex: number;
    private currentStatus: AdItemStatus;
    private controlsPromise: Promise<AdItemControls>;

    public readonly isGroup: boolean = true;
    public readonly isEmpty: boolean = false;
    /**
     * Builds a new GroupedVASTAdItems instance 
     * @param items items to group
     * @param preloadStrategy defines group preload strategy
     */
    constructor(private items: ImaAdItem<VASTTagAdUnit>[], 
                private itemPrepareStrategy: ItemPrepareStrategy,
                private successCriteria: GroupSuccessCriteria = GroupSuccessCriteria.AT_LEAST_ONE
                ) {
        this.currentIndex = 0;
        this.wraps = null;
        this.controlsPromise = null;
        this.currentStatus = AdItemStatus.INITIALIZED;

    }




    public get units(): VASTTagAdUnit[] {
        return this.items.map(el => el.units[0]);
    }
    public get status(): AdItemStatus {
        return this.currentStatus;
    }


    private internalLoad(): Promise<void> {
        this.wraps = this.items.map(el =>() => el.load());
        return Promise.resolve();
    }

    /**
     * loads the first item or all of the items (in a fire and forget fashion) if 
     * `preloadAllOnLoad` is true.
     * @returns 
     */
    load(): Promise<WrappedImaAdsManager> {
        return this.internalLoad().then(_ => this.wraps[this.currentIndex]());
    }

   
    /**
     * does nothing
     */
    prepare(): void {
        
    }

    /**
     * starts and chains items executions
     * @returns 
     */
    start(): Promise<AdItemControls> {
        if (!this.controlsPromise) {
            this.currentStatus = AdItemStatus.PLAYING;
            this.controlsPromise = this.internalLoad().then(_ => {

                const toEventuallyDestroy: Promise<AdItemControls>[] = [];
                let alreadyCompleted: boolean = false;
                const controlCache: Promise<AdItemControls>[] = this.items.map((e) => null);
                const ctrls: (() => Promise<AdItemControls>)[] = this.items.map((item, idx) => {
                    return () => {
                        // this.currentIndex = idx;
                        if (!controlCache[idx]) {
                            this.currentIndex = idx;
                            console.log("[AD-EVT] EXECUTING UNIT", item.units, "idx", this.currentIndex);
                            const el = item.start();
                            toEventuallyDestroy.push(el);
                            controlCache[idx] = el;
                        }

                        return controlCache[idx];
                    }
                });

                const completionPromise: Promise<AdItemStatus> = new Promise((resolve) => {
                    ctrls.reduce<Promise<AdItemStatus[]>>((prevRes, next, idx) => {
                        return prevRes.then(acc => {

                            return next().then(e => {

                                if(this.itemPrepareStrategy == ItemPrepareStrategy.BEFORE_PREV_ITEM_END){
                                    const nextIndex = idx + 1;
                                    if(nextIndex < this.items.length) {
                                        const myListener = (data: ProgressData) => {
                                            if(data.progress >= 80) {
                                                this.items[nextIndex].prepare();
                                                e.offProgress(myListener);
                                            }
                                        }
                                        e.onProgress(myListener)
                                    }
                                }
                                return e.completionPromise.then(re => {
                                    acc.push(re);
                                    return acc;
                                });
                            })
                        });
                    }, Promise.resolve([])).then(finalStatuses => {
                        alreadyCompleted = true;
                        let outval = AdItemStatus.ENDED;
                        if (finalStatuses.length > 0) {
                            const errors = finalStatuses.filter(e => e == AdItemStatus.PLAY_ERROR || e == AdItemStatus.LOAD_ERROR);
                            if (errors.length >= finalStatuses.length ||
                                (errors.length > 0 && this.successCriteria == GroupSuccessCriteria.ALL)) {
                                outval = errors.pop();
                            }
                        }
                        resolve(outval);
                    });

                });
                const currentItemControls = () => {
                    return ctrls[this.currentIndex]();
                }
                return {
                    completionPromise: completionPromise,
                    adInfo: () => currentItemControls().then(c => c.adInfo()),
                    close: () => currentItemControls().then(c => c.close()),
                    pause: () => {
                        return currentItemControls().then(c => c.pause());
                    },
                    destroy: () => {
                        return Promise.all(toEventuallyDestroy.map(e => e.then(c => c.destroy()))).then();
                    },
                    resume: () => {
                        return currentItemControls().then(c => c.resume());
                    },
                    togglePlay: () => {
                        return currentItemControls().then(c => c.togglePlay());
                    },
                    toggleAudio: () => {
                        return currentItemControls().then(c => c.toggleAudio());
                    },
                    alreadyCompleted: () => {
                        return alreadyCompleted;
                    },
                    volume: (volume?: number) => {
                        return currentItemControls().then(c => c.volume(volume));
                    },
                    unmute: () => {
                        return currentItemControls().then(c => c.unmute());
                    },
                    muted: (enable?: boolean) => {
                        return currentItemControls().then(c => c.muted(enable));
                    },
                    onProgress: (lst: ProgressDataListener) => {

                    },
                    offProgress: (lst: ProgressDataListener) => {

                    }
                }



            });
        }

        return this.controlsPromise;
    }



}